import type {
	ActivityLogOut,
	DataAccessRequestStatus,
} from '@repo/api-codegen';
import type { IconNames } from '@repo/foundations';
import type { ColorNames } from '@repo/theme/utils';
import {
	DATA_ACCESS_REQUEST_STATUS_ICON_MAP,
	INTEGRATION_JOB_EVENTS,
	NOTIFICATION_EVENT_TYPES,
	RESOURCE_TYPE_ICON_MAP,
} from '../constants';
import type { EnhancedNotification } from '../types';
import {
	getTeamAccessRequestStatusDisplay,
	isTeamAccessRequestEvent,
} from './teamAccessRequestUtils';

export interface IconConfig {
	iconName: IconNames;
	color: ColorNames;
	fillColor?: ColorNames;
	label: string;
	invisible: boolean;
}

function getMostRecentActivity(
	activityLogs: ActivityLogOut[]
): ActivityLogOut | null {
	if (!activityLogs.length) return null;

	const sortedLogs = [...activityLogs].sort(
		(a, b) => new Date(b.logged_at).getTime() - new Date(a.logged_at).getTime()
	);

	return sortedLogs[0] || null;
}

function getDataAccessRequestIcon(
	notification: EnhancedNotification
): IconConfig | null {
	if (!notification.computedMetadata.isDataAccessRequest) return null;

	const requestStatus = (notification.computedMetadata.dataAccessRequest
		?.status ?? 'pending') as DataAccessRequestStatus;

	const state: 'open' | 'active' | 'denied' | 'closed' = (() => {
		switch (requestStatus) {
			case 'pending':
				return 'open';
			case 'approved':
				return 'active';
			case 'rejected':
			case 'cancelled':
				return 'denied';
			case 'expired':
			case 'deleted':
			default:
				return 'closed';
		}
	})();

	const iconConfig = DATA_ACCESS_REQUEST_STATUS_ICON_MAP[requestStatus];
	if (!iconConfig) return null;

	return {
		iconName: iconConfig.iconName,
		color: iconConfig.color || 'icon/primary/default',
		fillColor: iconConfig.fillColor,
		label: `Data access request ${state}`,
		invisible: false,
	};
}

function getTeamAccessRequestIcon(
	notification: EnhancedNotification
): IconConfig | null {
	if (!isTeamAccessRequestEvent(notification.event)) return null;

	const status = getTeamAccessRequestStatusDisplay(
		notification.event,
		notification.metadata?.status as string | undefined
	);

	return {
		iconName: status.iconName,
		color: status.color ?? 'icon/primary/default',
		fillColor: status.fillColor,
		label: `Team access request ${status.label.toLowerCase()}`,
		invisible: false,
	};
}

function getIncidentIcon(
	notification: EnhancedNotification
): IconConfig | null {
	const { incident } = notification.computedMetadata;
	if (!incident) return null;

	if (incident.status === 'resolved') {
		return {
			iconName: 'circleCheckFilled',
			fillColor: 'icon/success/default',
			color: 'icon/success/default',
			label: 'Incident resolved',
			invisible: false,
		};
	}

	return {
		iconName: 'alertTriangleFilled',
		fillColor: 'icon/warning/default',
		color: 'icon/warning/default',
		label: 'Incident',
		invisible: false,
	};
}

function getSuccessIcon(
	notification: EnhancedNotification,
	mostRecentActivity: ActivityLogOut | null
): IconConfig | null {
	const isQuestionAnswered =
		mostRecentActivity?.activity_type === 'MARK_QUESTION_REPLY_AS_ANSWER' ||
		notification.event === 'question_answer';

	// We do not surface integration success in Inbox; only failures are shown
	if (isQuestionAnswered) {
		return {
			iconName: 'circleCheckFilled',
			fillColor: 'icon/success/default',
			color: 'icon/success/default',
			label: 'Question answered',
			invisible: false,
		};
	}

	return null;
}

function getFailureIcon(notification: EnhancedNotification): IconConfig | null {
	// For integration events, derive failure from the event itself, not current status
	const isIntegrationFailureEvent =
		notification.computedMetadata?.isIntegration &&
		INTEGRATION_JOB_EVENTS.has(notification.event);

	if (isIntegrationFailureEvent) {
		return {
			iconName: 'alertCircleFilled',
			color: 'icon/critical/default',
			fillColor: 'icon/critical/default',
			label: 'Integration sync failed',
			invisible: false,
		};
	}

	return null;
}

function getAiAgentStatusIcon(
	notification: EnhancedNotification
): IconConfig | null {
	if (!notification.computedMetadata?.isAiAgent) return null;

	switch (notification.event) {
		case 'ai_task_run_completed':
			return {
				iconName: 'circleCheckFilled',
				color: 'icon/success/default',
				fillColor: 'icon/success/default',
				label: 'AI task completed successfully',
				invisible: false,
			};
		case 'ai_task_run_failed':
			return {
				iconName: 'alertCircleFilled',
				color: 'icon/critical/default',
				fillColor: 'icon/critical/default',
				label: 'AI task failed',
				invisible: false,
			};
		case 'ai_task_run_timeout':
			return {
				iconName: 'clock',
				color: 'icon/warning/default',
				fillColor: undefined,
				label: 'AI task timed out',
				invisible: false,
			};
		default:
			return null;
	}
}

function getQuestionIcon(
	notification: EnhancedNotification
): IconConfig | null {
	if (notification.computedMetadata?.isQuestion) {
		return {
			iconName: 'circle',
			color: 'icon/warning/default',
			fillColor: undefined,
			label: 'Question',
			invisible: false,
		};
	}

	return null;
}

function getEventBasedIcon(
	notification: EnhancedNotification
): IconConfig | null {
	// Handle specific events that should have more specific icons
	const specificEventIcons: Record<string, IconNames> = {
		table_schema_changed: 'table',
		tables_dropped: 'table',
		resource_definition_updated: 'schema',
		resource_description_updated: 'file',
		resource_tag_added: 'tag',
		resource_tag_removed: 'tag',
		resource_collection_added: 'listDetails',
		resource_collection_removed: 'listDetails',
		resource_archived: 'archive',
		resource_published: 'check',
		resource_unpublished: 'x',
		resource_owner_assigned: 'users',
	};

	const specificIcon = specificEventIcons[notification.event];
	if (specificIcon) {
		return {
			iconName: specificIcon,
			color: 'icon/primary/default',
			fillColor: undefined,
			label: 'Resource update',
			invisible: false,
		};
	}

	// Fall back to the general event type mapping
	const eventTypeConfig = NOTIFICATION_EVENT_TYPES.find((config) =>
		config.values.includes(notification.event)
	);

	if (!eventTypeConfig) {
		return null;
	}

	return {
		iconName: eventTypeConfig.iconName,
		color: 'icon/primary/default',
		fillColor: undefined,
		label: eventTypeConfig.label,
		invisible: false,
	};
}

function getDefaultIcon(notification: EnhancedNotification): IconConfig {
	const entityIcon = RESOURCE_TYPE_ICON_MAP[notification.resource_type];

	if (!entityIcon) {
		// Try to get icon based on event type when resource_type is null
		const eventBasedIcon = getEventBasedIcon(notification);
		if (eventBasedIcon) {
			return eventBasedIcon;
		}

		// Fallback to generic circle icon
		return {
			iconName: 'circle',
			color: 'icon/secondary/default',
			fillColor: undefined,
			label: 'Notification',
			invisible: true,
		};
	}

	return {
		iconName: entityIcon,
		color: 'icon/primary/default',
		fillColor: undefined,
		label: 'Notification',
		invisible: false,
	};
}

export function getNotificationIconConfig(
	notification: EnhancedNotification
): IconConfig {
	const { activityLogs } = notification.computedMetadata;
	const mostRecentActivity = getMostRecentActivity(activityLogs);

	const iconConfigs = [
		getDataAccessRequestIcon,
		getTeamAccessRequestIcon,
		getIncidentIcon,
		(n: EnhancedNotification) => getSuccessIcon(n, mostRecentActivity),
		getFailureIcon,
		getAiAgentStatusIcon,
		getQuestionIcon,
		getDefaultIcon,
	];

	for (const getIconConfig of iconConfigs) {
		const iconConfig = getIconConfig(notification);
		if (iconConfig) return iconConfig;
	}

	return getDefaultIcon(notification);
}
